// covered-by: frontends/carrie/src/ask-v2/rail/__tests__/toolStepLabel.test.ts

import i18n from "../../i18n/config";

/**
 * The warhol tools whose chip names are house copy, by the slug on `step.tool`.
 *
 * `displayName` is English by construction: warhol's `runtime/live_steps.py`
 * and christo's `warholToolDisplayName` either humanize the slug ("Write plan")
 * or copy `DISPLAY_NAMES`, and neither knows the reader's language. The chip is
 * the one place that turns the slug into words, the same split
 * `ask-v2/rail/checkNames.ts` makes for check names.
 *
 * why these stay ahead of `agentChips.tools`: the rail's in-flight row and the
 * thread chip share this function, and `intents_from_drafts` is the sentence
 * "Reuniendo los hallazgos de los revisores", not the catalog's imperative.
 */
const TOOL_NAME_KEY: Record<string, string> = {
  write_plan: "writePlan",
  intents_from_drafts: "intentsFromDrafts",
  load_capability: "loadSkill",
  run_python: "runPython",
  ocr_pages: "ocrPages",
};

type NamedStep = {
  tool?: string | null;
  displayName?: string | null;
  statusName?: string | null;
};

type ChipBundles = {
  agentChips?: {
    tools?: Record<string, unknown>;
    agents?: Record<string, unknown>;
  };
};

function catalogFromTranslator(
  tool: string,
  t: (key: string) => string,
): string {
  const catalogKey = `agentChips.tools.${tool}`;
  const catalog = t(catalogKey);
  if (catalog && catalog !== catalogKey) return catalog;
  return "";
}

function bundleLine(
  language: string,
  section: "tools" | "agents",
  slug: string,
): string {
  const bundle: unknown = i18n.getResourceBundle(language, "ui");
  // why: typeof null is "object", and a function can carry agentChips, so either check alone names the chip from the wrong bundle
  if (bundle == null || typeof bundle !== "object") return "";
  const line = (bundle as ChipBundles).agentChips?.[section]?.[slug];
  return typeof line === "string" ? line.trim() : "";
}

/**
 * The chip catalog for one saved language, with no English fallback.
 *
 * why not `t()`: i18next's `fallbackLng` is English, so a slug missing from
 * the active locale comes back as the English catalog line (or the stored
 * `displayName` if we then give up). A Spanish UI was showing "Claims registry".
 */
function languageBase(tag: string): string {
  return tag.trim().toLowerCase().split("-")[0];
}

function catalogFromBundle(language: string, tool: string): string {
  const tag = language.trim();
  const fromTag =
    bundleLine(tag, "tools", tool) ||
    bundleLine(tag.toLowerCase(), "tools", tool);
  if (fromTag) return fromTag;
  const base = languageBase(tag);
  if (base && base !== tag.toLowerCase())
    return bundleLine(base, "tools", tool);
  return "";
}

/**
 * The thread chip for one subagent slug, from `agentChips.agents`.
 *
 * Empty when that slug has no catalog line. The caller then uses the rail's
 * progress phrase. A non-English miss stays empty: i18next would otherwise
 * hand back the English catalog line.
 */
export function agentChipLabel(
  slug: string,
  t: (key: string) => string,
  language?: string,
): string {
  const name = slug.trim();
  if (!name) return "";
  const saved = (language ?? "").trim();
  const base = languageBase(saved);
  const english = language == null || base === "" || base === "en";
  if (english) {
    const key = `agentChips.agents.${name}`;
    const catalog = t(key);
    if (catalog && catalog !== key) return catalog;
    return "";
  }
  const fromTag =
    bundleLine(saved, "agents", name) ||
    bundleLine(saved.toLowerCase(), "agents", name);
  if (fromTag) return fromTag;
  if (base && base !== saved.toLowerCase())
    return bundleLine(base, "agents", name);
  return "";
}

/**
 * A tool step's chip name: the status the backend set, else the house copy for
 * a known tool, else the slug catalog, else the backend's display name.
 *
 * why the status first: a backend that sets one words the step for this very
 * call ("Searching the spec"), which a name for the tool cannot.
 *
 * `language` is the reader's saved UI language. Omitted means English, so a
 * unit test whose `t` is a fake still decides the catalog. A catalog hit beats
 * `displayName`. A miss falls through to it, including for a non-English
 * reader: a blank label unnames the chip and drops the plan row.
 */
export function toolStepLabel(
  step: NamedStep,
  t: (key: string) => string,
  language?: string,
): string {
  if (step.statusName) return step.statusName;
  const house = step.tool ? TOOL_NAME_KEY[step.tool] : undefined;
  if (house) return t(`agentSteps.toolNames.${house}`);
  const saved = (language ?? "").trim();
  const base = languageBase(saved);
  const english = language == null || base === "" || base === "en";
  if (step.tool) {
    const catalog = english
      ? catalogFromTranslator(step.tool, t)
      : catalogFromBundle(saved, step.tool);
    if (catalog) return catalog;
  }
  return step.displayName ?? "";
}
